
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "When I think about money, I feel ___ because ___.",
    options: [
      "'Money makes me happy because ___.'",
      "'I need more money to feel ___.'",
      "'Money solves all my problems because ___.'"
    ],
    correctAnswer: 0,
    explanation: "The Money Mindset Reflection activity uses the prompt 'When I think about money, I feel ___ because ___.' to help identify your emotional relationship with money and how early experiences shape your financial beliefs."
  },
  {
    question: "What is the primary purpose of the IFDFW assessment?",
    options: [
      "To measure your financial well-being across multiple dimensions",
      "To calculate your credit score",
      "To determine your investment portfolio"
    ],
    correctAnswer: 0,
    explanation: "The IFDFW (I Feel Damned Wealthy) assessment evaluates financial well-being across multiple dimensions including financial security, freedom, and overall satisfaction with your financial situation."
  },
  {
    question: "Which factor has the strongest influence on financial well-being?",
    options: [
      "Your mindset and beliefs about money",
      "The amount of money in your bank account",
      "The brand of your car"
    ],
    correctAnswer: 0,
    explanation: "While financial resources are important, research shows that your mindset and beliefs about money have a stronger influence on financial well-being than the actual amount of money you have."
  },
  {
    question: "What is 'financial freedom' according to financial wellness research?",
    options: [
      "Having enough income to cover your needs and pursue your goals",
      "Never having to work again",
      "Owning multiple properties"
    ],
    correctAnswer: 0,
    explanation: "Financial freedom means having sufficient income and resources to meet your basic needs, handle emergencies, and pursue your life goals without constant financial stress."
  },
  {
    question: "How do early money experiences typically shape adult financial behavior?",
    options: [
      "They create unconscious beliefs that influence spending and saving habits",
      "They determine your career choice",
      "They affect your social status"
    ],
    correctAnswer: 0,
    explanation: "Early experiences with money, often learned from family and childhood, create unconscious beliefs and patterns that significantly influence adult financial behaviors, spending habits, and attitudes toward money."
  }
];

const FinancialModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('financialM1');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  React.useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 1 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>The foundations of financial well-being</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Money mindset and beliefs</span>
              </li>
              <li>
                <span className="check"></span>
                <span>The IFDFW assessment framework</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your financial wellness journey by exploring the psychology of financial stress!</p>
            <p>Ready to understand how financial stress affects your well-being?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/financial-resources')}
          >
            Return to Financial Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left financial-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Understanding Financial Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about financial well-being foundations.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/financial-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule1QuizPage;

